import axios from 'axios'
import {
  PaymentBuyer,
  PaymentDetailRecord,
  PaymentMode,
  PaymentRecord,
  PaymentStatus,
} from '../../domain/entities/Payment'
import { GetPaymentsFilters, GetPaymentsResponse, PaymentRepository } from '../../domain/repositories/PaymentRepository'
import {
  EnrichedPaymentApi,
  PayerSummaryApi,
  paymentApiDatasource,
  PaymentDetailApi,
} from '../datasources/PaymentApiDatasource'

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined
    return data?.message || data?.error || fallback
  }

  return err instanceof Error ? err.message : fallback
}

const toBuyer = (payer?: PayerSummaryApi): PaymentBuyer | undefined =>
  payer
    ? {
        id: payer.id,
        name: payer.name,
        businessName: payer.business_name,
        phone: payer.phone,
        email: payer.email,
        role: payer.role,
      }
    : undefined

const toRecord = (p: EnrichedPaymentApi): PaymentRecord => ({
  id: p.id,
  orderId: p.order_id,
  paymentMode: p.payment_mode as PaymentMode,
  qrRef: p.qr_ref,
  screenshotUrl: p.screenshot_url,
  amount: p.amount,
  status: p.status as PaymentStatus,
  verifiedBy: p.verified_by,
  verifiedAt: p.verified_at,
  createdAt: p.created_at,
  updatedAt: p.updated_at,
  buyer: toBuyer(p.payer),
})

const toDetailRecord = (p: PaymentDetailApi): PaymentDetailRecord => ({
  ...toRecord(p),
  buyerAddress: p.payer_address,
})

export class PaymentRepositoryImpl implements PaymentRepository {
  public async getPayments(filters: GetPaymentsFilters): Promise<GetPaymentsResponse> {
    try {
      const res = await paymentApiDatasource.getPaymentsEnriched({
        skip: (filters.page - 1) * filters.limit,
        limit: filters.limit,
        status: filters.status,
        search: filters.search || undefined,
      })
      return {
        payments: res.data.map(toRecord),
        totalItems: res.total,
        totalPages: Math.max(1, Math.ceil(res.total / filters.limit)),
      }
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load payments.'))
    }
  }

  public async getPaymentDetail(id: string): Promise<PaymentDetailRecord> {
    try {
      const detail = await paymentApiDatasource.getPaymentDetail(id)
      return toDetailRecord(detail)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load payment detail.'))
    }
  }

  public async verifyPayment(id: string): Promise<PaymentRecord> {
    try {
      const res = await paymentApiDatasource.verifyPayment(id)
      return toRecord(res)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to verify payment.'))
    }
  }

  public async rejectPayment(id: string, reason: string): Promise<void> {
    try {
      await paymentApiDatasource.rejectPayment(id, { reason })
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to reject payment.'))
    }
  }

  public async recordOfflinePayment(
    payment: Omit<PaymentRecord, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'buyer'>,
  ): Promise<PaymentRecord> {
    try {
      const res = await paymentApiDatasource.uploadPayment({
        order_id: payment.orderId,
        amount: payment.amount,
        payment_mode: payment.paymentMode || 'cash',
        qr_ref: payment.qrRef,
        screenshot_url: payment.screenshotUrl,
      })
      return toRecord(res)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to record payment.'))
    }
  }
}

export const paymentRepository = new PaymentRepositoryImpl()
